import { Component, OnInit } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs';
import { State } from 'src/app/core/store';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { PayrollRejectionReasonModel } from 'src/app/models/payrollRejectionReasonModel';
import {
  deletePayrollRejectionReason,
  deletePayrollRejectionReasonSuccess,
  loadPayrollRejectionReasons,
  savePayrollRejectionReason,
  savePayrollRejectionReasonSuccess
} from 'src/app/core/store/payroll-approval/payroll-approval.actions';
import { AlertService } from 'src/app/shared/services/alert.service';

@Component({
    selector: 'app-payroll-rejection-reasons',
    templateUrl: './payroll-rejection-reasons.component.html',
    styleUrls: ['./payroll-rejection-reasons.component.scss'],
    standalone: false
})
export class PayrollRejectionReasonsComponent extends BaseComponent implements OnInit {
  reasons: PayrollRejectionReasonModel[] = [];
  loading: boolean;
  saving: boolean;
  canManage: boolean;
  newReason = '';

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private alertService: AlertService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subs.push(
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canManage = permissionIDs && !!permissionIDs.find(p => p === 154); //PayrollApproval_Approve
      }),
      this.store.select(s => s.payrollApprovalState.payrollRejectionReasons).subscribe(reasons => {
        this.reasons = reasons || [];
      }),
      this.store.select(s => s.payrollApprovalState.loadingPayrollRejectionReasons).subscribe(loading => {
        this.loading = loading;
      }),
      this.store.select(s => s.payrollApprovalState.savingPayrollRejectionReason).subscribe(saving => {
        this.saving = saving;
      }),
      this.actions$.pipe(
        ofType(savePayrollRejectionReasonSuccess),
        tap(() => {
          this.alertService.success("Success");
          this.newReason = '';
        })
      ).subscribe(),
      this.actions$.pipe(
        ofType(deletePayrollRejectionReasonSuccess),
        tap(() => {
          this.alertService.success("Success");
        })
      ).subscribe(),
    );

    this.store.dispatch(loadPayrollRejectionReasons());
  }

  addReason() {
    const reasonText = this.newReason.trim();
    if (!reasonText) {
      return;
    }

    this.store.dispatch(savePayrollRejectionReason({
      reason: {
        id: 0,
        reason: reasonText,
        sortOrder: this.getNextSortOrder(),
        isActive: true,
        isDefault: false
      }
    }));
  }

  saveReason(reason: PayrollRejectionReasonModel) {
    if (!this.canSaveReason(reason)) {
      return;
    }

    this.store.dispatch(savePayrollRejectionReason({
      reason: {
        ...reason,
        reason: reason.reason.trim(),
        isActive: true
      }
    }));
  }

  removeReason(reason: PayrollRejectionReasonModel) {
    if (!this.canManage || reason.isDefault) {
      return;
    }

    this.alertService.confirm('Remove Reason', 'Remove this rejection reason?')
      .subscribe(answer => {
        if (answer) {
          this.store.dispatch(deletePayrollRejectionReason({ reasonID: reason.id }));
        }
      }, error => {
      });
  }

  canAddReason() {
    return this.canManage && !this.saving && this.newReason.trim().length > 0;
  }

  canSaveReason(reason: PayrollRejectionReasonModel) {
    return this.canManage && !this.saving && !reason.isDefault && !!reason.reason && reason.reason.trim().length > 0;
  }

  getNextSortOrder() {
    const maxSortOrder = this.reasons
      .filter(reason => !reason.isDefault)
      .reduce((max, reason) => Math.max(max, reason.sortOrder), 0);

    return maxSortOrder + 10;
  }
}
